import _, { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ToastComponent from '../components/toast';
import { selectToken } from '../pages/auth/authSlice';
import { type ApiError, OpenAPI } from '../services/wealthlane-award-services';
import { selectTenantId } from '../pages/configuration/customerSwitchUserSlice';
import useInstance from './custom-hooks/useInstance';
import { attachDecimalConfigToRequest } from '../utils/apiDecimalConfig';
import { showErrorModal } from '../components/error-modal/errorModalEmitter';

export function useWealthlaneAwardApi(): {
  dismissError: () => void;
  error: ApiError | undefined;
  isLoading: boolean;
  handleRequest: <T>(request: Promise<T>, grantDoc?: boolean) => Promise<T | undefined>;
} {
  const token = useSelector(selectToken);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { getRoleId, getTenantById } = useInstance();

  OpenAPI.BASE = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
  OpenAPI.TOKEN = token?.toString();
  // OpenAPI.HEADERS = {
  //   [`X-Tenant-Id`]: tenantId === null ? '' : (tenantId as string),
  //   ['X-Switch-Source-Role-Id']: window.localStorage.getItem('x-switch-role-id') ?? '',
  // };
  OpenAPI.HEADERS = {
    [`X-Tenant-Id`]: getTenantById()?.tenantId ?? '',
    ['X-Switch-Source-Role-Id']: getRoleId() ?? '',
  };

  const handleRequest = useCallback(async function <T>(
    request: Promise<T>,
    grantDoc: boolean = false
  ) {
    setIsLoading(true);

    // Transfer decimal config metadata from promise to axios interceptor
    attachDecimalConfigToRequest(request);

    try {
      const response = await request;
      setError(undefined);
      return response;
    } catch (exception: any) {
      setError(exception);
      if (exception?.body?.error?.validationErrors != null) {
        const errorMessages: string[] = [];

        // Process each validation error and split by newlines
        exception.body.error.validationErrors.forEach((validationError: any) => {
          const message = validationError.message || '';
          // Split message by \n and filter out empty strings
          const splitMessages = message.split('\n').filter((msg: string) => msg.trim() !== '');
          errorMessages.push(...splitMessages);
        });

        // Check if any error messages should be ignored
        const shouldIgnore = errorMessages.some(
          (msg: string) =>
            msg.includes('OTP is invalid') ||
            msg.includes('Invalid Template') ||
            msg.includes('Only excel file can be uploaded')
        );

        if (!shouldIgnore && errorMessages.length > 0) {
          // Display each error message separately
          errorMessages.forEach((errorMsg: string) => {
            if (errorMsg.trim() !== '') {
              toast.error(<ToastComponent label='Error' message={errorMsg.trim()} />, {
                autoClose: 30000,
              });
            }
          });
        }
      } else if (
        exception?.body?.error?.message?.includes('Index was outside the bounds of the array.') ===
          true ||
        exception?.body?.error?.message?.includes('Only excel file can be uploaded') === true ||
        exception?.body?.error?.message?.includes('Invalid Template') === true
      ) {
        /* empty */
      } else if (exception?.body?.error != null) {
        // Handle main error message that might contain newlines
        const mainMessage = exception?.body?.error?.message ?? '';
        const splitMessages = mainMessage.split('\n').filter((msg: string) => msg.trim() !== '');

        if (splitMessages.length > 1) {
          // Multiple messages separated by newlines
          splitMessages.forEach((errorMsg: string) => {
            if (errorMsg.trim() !== '') {
              toast.error(<ToastComponent label='Error' message={errorMsg.trim()} />, {
                autoClose: 30000,
              });
            }
          });
        } else {
          // Single message
          toast.error(<ToastComponent label='Error' message={mainMessage} />, { autoClose: 30000 });
        }
      } else if (
        exception.status === 401 ||
        (exception.status === 403 && isEmpty(exception?.body))
      ) {
        /* empty */
      } else if (!_.isEmpty(exception?.body?.error?.message)) {
        const mainMessage = exception?.body?.error?.message ?? '';
        const splitMessages = mainMessage.split('\n').filter((msg: string) => msg.trim() !== '');

        if (splitMessages.length > 1) {
          // Multiple messages separated by newlines
          splitMessages.forEach((errorMsg: string) => {
            if (errorMsg.trim() !== '') {
              toast.error(<ToastComponent label='Error' message={errorMsg.trim()} />, {
                autoClose: 30000,
              });
            }
          });
        } else {
          // Single message
          toast.error(<ToastComponent label='Error' message={mainMessage} />, {
            autoClose: 30000,
          });
        }
      } else {
        // toast.error(
        //   <ToastComponent
        //     label='Error'
        //     message={'Server is down at the moment. Please try again after sometime.'}
        //   />,
        //   {
        //     toastId: 2,
        //     autoClose: false,
        //   }
        // );
        showErrorModal({ status: exception?.status });
      }
      throw Error(exception);
    } finally {
      setIsLoading(false);
    }
  },
  []);

  function dismissError(): void {
    setError(undefined);
  }

  return { dismissError, error, isLoading, handleRequest };
}

export default useWealthlaneAwardApi;
